<template>
    <UILoader v-if="isHoldingLoading || isDealersLoading" class="modalable" />

    <div v-else class="holding-modal">
        <div class="holding-modal__header">
            <div class="holding-modal__name">
                {{ holding?.name }}
            </div>

            <router-link
                v-if="holding"
                :to="{ name: 'EditHolding', params: { holding_id: holding.id } }"
                class="holding-modal__menu"
            >
                <img v-svg-inline src="@/assets/icons/ui/edit.svg" />
            </router-link>
        </div>

        <div class="holding-modal__body">
            <div class="holding-modal__details">
                <div class="holding-modal__detail">
                    <div class="holding-modal__detail-label">SAP-код</div>
                    <div class="holding-modal__detail-value">
                        {{ holding?.sap_code ?? '' }}
                    </div>
                </div>

                <div class="holding-modal__detail">
                    <div class="holding-modal__detail-label">Количество ДЦ</div>
                    <div class="holding-modal__detail-value">
                        {{ arDealers.length }}
                    </div>
                </div>

                <div class="holding-modal__detail">
                    <div class="holding-modal__detail-label">Статус</div>
                    <div class="holding-modal__detail-value">
                        {{ holding?.status ? getHoldingStatusLabel(holding.status) : '' }}
                    </div>
                </div>
            </div>

            <div class="holding-modal__table">
                <DataTable
                    :headers="headers"
                    :data="arDealers"
                    has-actions
                    @sortable="changeSort"
                >
                    <template #site="{ row }">
                        <a
                            v-if="row.site"
                            :href="row.site"
                            target="_blank"
                            @click.stop
                        >
                            <img v-svg-inline src="@/assets/icons/arrowGoLink.svg" />
                        </a>
                    </template>

                    <template #actions="{ row }">
                        <a
                            :href="router.resolve({ name: 'EditDealer', params: { dealer_id: row.id } }).href"
                            class="holding-modal__table-menu"
                        >
                            <img v-svg-inline src="@/assets/icons/ui/edit.svg" />
                        </a>
                    </template>
                </DataTable>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { ref, onMounted } from 'vue';

    import { injectStrict } from '@/injection/injectStrict.ts';
    import { RouterKey } from '@/injection/keys.ts';

    import { DealerListItem } from '@/types/dealer.type.ts';
    import { getHoldingStatusLabel, Holding } from '@/types/holding.type.ts';

    import useHoldingRepository from '@/repositories/holding/useHoldingRepository.ts';
    import useLoading from '@/utils/useLoading.ts';

    import { sortBy } from '@/utils/collection-helpers.ts';

    import DataTable from '@/components/shared/DataTable/DataTable.vue';

    import UILoader from '@/components/ui/UILoader/UILoader.vue';

    const { holdingId } = defineProps<{ holdingId: number }>();

    const router = injectStrict(RouterKey);

    const { getHolding, getHoldingDealers } = useHoldingRepository();
    const { isLoading: isHoldingLoading, setLoading: setHoldingLoading } = useLoading();
    const { isLoading: isDealersLoading, setLoading: setDealersLoading } = useLoading();

    const holding = ref<Holding | null>(null);
    const arDealers = ref<DealerListItem[]>([]);
    const headers = ref([
        { label: 'Название ДЦ', key: 'name', sortable: true },
        { label: 'Юр. наименование', key: 'legal_entity_name' },
        { label: 'Код', key: 'sap_code' },
        { label: 'Сайт', key: 'site', sortable: false },
    ]);

    function changeSort(key: string, dir: 'asc' | 'desc'): void {
        arDealers.value = sortBy(arDealers.value, key, dir);
    }

    function fetchDealers(): void {
        setDealersLoading(true);

        getHoldingDealers(holdingId)
            .then((dealers: DealerListItem[]) => {
                arDealers.value = sortBy(dealers, 'name', 'asc');
                setDealersLoading(false);
            });
    }

    function fetchHolding(): void {
        setHoldingLoading(true);

        getHolding(holdingId).then((fetchedHolding: Holding) => {
            holding.value = fetchedHolding;
            setHoldingLoading(false);
        });

        fetchDealers();
    }

    onMounted(async () => {
        fetchHolding();
    });
</script>

<style scoped lang="scss">
    @use 'HoldingModal' as *;
</style>
